import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import { Modal } from '../../../components/shared/Modal';
import { useToast } from '../../../components/shared/Toast';
import { authenticatedFetch } from '../../../lib/api';
import { AgentForm } from './AgentForm';
import { ClearFiltersButton, TableBodySkeleton, erpPageBtnPrimaryClass } from '@euroasiann/shared/ui';
import {
    pmTableWrapClass,
    pmThClass,
    pmTdClass,
    pmTrClass,
    pmIconBtnClass,
    pmDangerIconBtnClass,
    pmStatusClass,
} from '../port-management.ui';

interface Agent {
    _id: string;
    companyName: string;
    contactPerson: string;
    email: string;
    phone: string;
    workingHours: string;
    supportedPorts: {
        _id: string;
        name: string;
        code: string;
    }[];
    status: string;
}

export function AgentList() {
    const [search, setSearch] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
    const { success, error } = useToast();
    const queryClient = useQueryClient();

    const { data: agents = [], isLoading } = useQuery({
        queryKey: ['agents'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/agents');
            const data = await res.json();
            return data.data || [];
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await authenticatedFetch(`/api/v1/agents/${id}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error('Failed to delete agent');
        },
        onSuccess: () => {
            success('Agent deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['agents'] });
        },
        onError: (err: any) => {
            error(err.message);
        },
    });

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this agent?')) {
            deleteMutation.mutate(id);
        }
    };

    const filteredAgents = agents.filter((a: Agent) =>
        a.companyName.toLowerCase().includes(search.toLowerCase()) ||
        a.contactPerson.toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="relative max-w-xl flex-1">
                        <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                        <input
                            type="search"
                            placeholder="Search agents"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-10 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-0 pl-9 pr-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
                        />
                    </div>
                    <ClearFiltersButton visible={!!search.trim()} onClick={() => setSearch('')} variant="toolbar" />
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setSelectedAgent(null);
                        setIsFormOpen(true);
                    }}
                    className={erpPageBtnPrimaryClass}
                >
                    <MdAdd className="h-5 w-5" />
                    Add agent
                </button>
            </div>

            <div className={pmTableWrapClass}>
                <table className="w-full min-w-[720px] border-collapse">
                    <thead>
                        <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                            {['Agent', 'Contact', 'Ports', 'Status', 'Actions'].map((h) => (
                                <th key={h} className={pmThClass}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableBodySkeleton rows={6} cols={5} />
                        ) : filteredAgents.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">
                                    No agents found
                                </td>
                            </tr>
                        ) : filteredAgents.map((a: Agent) => (
                            <tr key={a._id} className={pmTrClass}>
                                <td className={pmTdClass}>
                                    <div className="font-medium">{a.companyName}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.contactPerson}</div>
                                </td>
                                <td className={pmTdClass}>
                                    <div>{a.email}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.phone}</div>
                                </td>
                                <td className={pmTdClass}>
                                    <div className="flex flex-wrap gap-1">
                                        {a.supportedPorts?.length
                                            ? a.supportedPorts.map((port) => (
                                                <span key={port._id} className="rounded-md bg-[hsl(var(--muted))] px-2 py-0.5 text-xs">{port.code}</span>
                                            ))
                                            : <span className="text-[hsl(var(--muted-foreground))]">No ports</span>}
                                    </div>
                                    {a.workingHours && (
                                        <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{a.workingHours}</div>
                                    )}
                                </td>
                                <td className={pmTdClass}>
                                    <span className={pmStatusClass(a.status === 'Active')}>{a.status}</span>
                                </td>
                                <td className={pmTdClass}>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedAgent(a);
                                                setIsFormOpen(true);
                                            }}
                                            className={pmIconBtnClass}
                                        >
                                            <MdEdit className="h-4 w-4" />
                                        </button>
                                        <button type="button" onClick={() => handleDelete(a._id)} className={pmDangerIconBtnClass}>
                                            <MdDelete className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                title={selectedAgent ? 'Edit agent' : 'Add agent'}
            >
                <AgentForm
                    initialData={selectedAgent}
                    onSuccess={() => {
                        setIsFormOpen(false);
                        queryClient.invalidateQueries({ queryKey: ['agents'] });
                    }}
                    onCancel={() => setIsFormOpen(false)}
                />
            </Modal>
        </div>
    );
}
